import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Loader2, Send, Brain, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';

interface ContextAwareChatProps {
  userId: string;
  currentProjectId: string;
  projectName?: string;
}

interface ContextDetails {
  interests: string[];
  preferences?: string[];
  recentInteractions?: Array<{
    text: string;
    timestamp: string;
    interactionId: string;
  }>;
  projectMetadata?: {
    projectName?: string;
    creationDate?: string;
    memberCount?: number;
  };
}

const ContextAwareChat: React.FC<ContextAwareChatProps> = ({
  userId,
  currentProjectId,
  projectName
}) => {
  const [query, setQuery] = useState('');
  const [response, setResponse] = useState('');
  const [contextData, setContextData] = useState<ContextDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isContextLoading, setIsContextLoading] = useState(false);

  // Fetch context when component mounts or projectId changes
  useEffect(() => {
    fetchContext();
  }, [currentProjectId, userId]);

  const fetchContext = async () => {
    if (!userId || !currentProjectId) return;
    
    setIsContextLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/knowledge-graph/tools/getContextDetails', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-User-ID': userId
        },
        body: JSON.stringify({ 
          projectId: currentProjectId,
          includeRecent: true,
          recentLimit: 3
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch context');
      }

      const data = await response.json();
      setContextData(data.data);
    } catch (error) {
      console.error('Error fetching context:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch context');
    } finally {
      setIsContextLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!query.trim() || isLoading) return;

    setIsLoading(true);
    setError(null);

    try {
      // First, ingest this interaction into the knowledge graph
      await fetch('/api/knowledge-graph/interactions', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-User-ID': userId
        },
        body: JSON.stringify({
          projectId: currentProjectId,
          text: query,
          timestamp: new Date().toISOString()
        }),
      });

      // Build context-enriched prompt
      const interests = contextData?.interests?.join(', ') || 'none identified';
      const preferences = contextData?.preferences?.join(', ') || 'none specified';
      const projectInfo = contextData?.projectMetadata?.projectName || currentProjectId;
      
      const contextPrompt = `User query: "${query}"

Context from project "${projectInfo}":
- Topics of interest: ${interests}
- User preferences: ${preferences}
- Project has ${contextData?.projectMetadata?.memberCount || 0} member(s)

Please provide a relevant response based on this context.`;

      // Here you would integrate with your existing LLM service
      // For now, we'll show the generated prompt
      setResponse(`Context-enriched prompt generated:

${contextPrompt}

[In a real implementation, this would be sent to your LLM API and the actual response would be displayed here]`);

      // Refresh context after interaction
      await fetchContext();
    } catch (error) {
      console.error('Error sending message:', error);
      setError(error instanceof Error ? error.message : 'Failed to send message');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="space-y-4">
      {/* Context Display */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm">
            <Brain className="h-4 w-4" />
            Project Context: {projectName || currentProjectId}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {isContextLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Loading context...
            </div>
          ) : contextData ? (
            <>
              {contextData.interests && contextData.interests.length > 0 && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground">
                    Topics of Interest:
                  </label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {contextData.interests.map((interest, index) => (
                      <Badge key={index} variant="secondary" className="text-xs">
                        {interest}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {contextData.preferences && contextData.preferences.length > 0 && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground">
                    Preferences:
                  </label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {contextData.preferences.map((pref, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {pref.replace('_', ' ')}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {contextData.projectMetadata && (
                <div className="text-xs text-muted-foreground">
                  Members: {contextData.projectMetadata.memberCount || 0} | 
                  {contextData.projectMetadata.creationDate && (
                    <> Created: {new Date(contextData.projectMetadata.creationDate).toLocaleDateString()}</>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="text-sm text-muted-foreground">
              No context data available for this project
            </div>
          )}
        </CardContent>
      </Card>

      {/* Chat Interface */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Context-Aware Chat</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Textarea
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask something about this project..."
              className="min-h-[100px]"
              disabled={isLoading}
            />
            <div className="flex justify-between items-center">
              <div className="text-xs text-muted-foreground">
                Press Enter to send, Shift+Enter for new line
              </div>
              <Button 
                onClick={handleSendMessage} 
                disabled={!query.trim() || isLoading}
                size="sm"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-3 w-3 mr-1" />
                    Send
                  </>
                )}
              </Button>
            </div>
          </div>

          {response && (
            <div className="border-t pt-4">
              <h4 className="font-medium mb-2">Response:</h4>
              <div className="bg-muted/50 p-3 rounded-md">
                <pre className="whitespace-pre-wrap text-sm">{response}</pre>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ContextAwareChat; 